import React, { useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import getAssetImageUrl from '../utils/assetImageUrl';
import formatAssetId from '../utils/formatAssetId';
import StatusBadge from './StatusBadge';
import { ChatToggle } from './ChatThread';
import WarehouseMap from './WarehouseMap';
import RepairSummaryReport from './RepairSummaryReport';
import AssetQRCode from './AssetQRCode';
import { Search, Filter, Eye, Bluetooth, MapPin, ClipboardCheck, Wrench, Ban, Box, X, Plus, Upload, Image, Package, Maximize2, Edit2, Check, ArrowRight, FileText, QrCode, Printer, Tag } from 'lucide-react';
import FeatureGate from './FeatureGate';
import SearchableSelect from './SearchableSelect';

// Predefined asset types based on the database schema and existing images
const ASSET_TYPES = [
  'Racks',
  'Racks (Large)',
  'Shipping Rack',
  'Material Handling (Rack)',
  'Metal Storage (Rack)',
  'Warehouse (Rack)',
  'Automotive Parts (Rack)',
  'Dunnage (Rack)',
  'Steel Rack (Dunnage)',
  'Bins (Large)',
  'Bins (Medium)',
  'Bins (Small)',
  'Returnable (Bin)',
  'Industrial Scrap (Bin)',
  'Metal Scrap Large (Bin)',
  'Metal Scrap Small (Bin)',
  'Metal Stacking (Bin)',
  'Crates',
  'Crates (Large)',
  'Crates (Small)',
  'Totes (Large)',
  'Totes (Medium)',
  'Totes (Small)',
  'Pallets',
  'Automotive Packaging (Dunnage)',
  'Custom Foam (Dunnage)',
  'Plastic (Dunnage)',
  'Returnable Package (Dunnage)',
  'Dunnage',
  'Custom'
];

const ASSET_CATEGORIES = [
  'Returnable',
  'Expendable',
  'Fixed',
  'Mobile'
];

const ASSET_STATUSES = ['Active', 'Missing', 'In-Transit', 'Needs Repair', 'Scrapped'];

export default function AssetManager() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    warehouses, 
    repairs, 
    loading, 
    createAsset, 
    scrapAsset, 
    updateAssetCapacity,
    updateAsset,
    createInspection,
    getAssetInspections,
    getAssetHistory,
    assignFiducialMarker,
    releaseFiducialMarker
  } = useApi();
  const initialAssetFilters = location.state?.assetFilters || {};
  const [searchTerm, setSearchTerm] = useState(initialAssetFilters.search || '');
  const [filterType, setFilterType] = useState(initialAssetFilters.type || 'All');
  const [filterStatus, setFilterStatus] = useState(initialAssetFilters.status || 'All');
  const [filterCategory, setFilterCategory] = useState(initialAssetFilters.category || 'All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showMiniMap, setShowMiniMap] = useState(true); // Auto-show map view
  const [showNewAssetModal, setShowNewAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showReleaseMarkerModal, setShowReleaseMarkerModal] = useState(false);
  const [assetToRelease, setAssetToRelease] = useState(null);
  const [showSummaryReport, setShowSummaryReport] = useState(null); // For Repair Summary Report
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [tempCapacity, setTempCapacity] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editingId, setEditingId] = useState(false);
  const [tempName, setTempName] = useState('');
  const [tempId, setTempId] = useState('');
  const fileInputRef = useRef(null);
  const inspectionFileInputRef = useRef(null);
  const csvFileInputRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importForm, setImportForm] = useState({
    warehouseId: '',
    type: ''
  });
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [newAssetForm, setNewAssetForm] = useState({
    id: '',
    name: '',
    type: '',
    category: '',
    warehouseId: '',
    zoneId: '',
    status: 'Active',
    capacity: '',
    brand: '',
    images: [],
    isTemplate: false
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' or 'template'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    inspectionType: 'Routine', // 'Routine' or 'Repair'
    status: 'Pending', // 'Pending', 'Passed', 'Failed'
    notes: '',
    findings: {},
    checklist: {
      structuralIntegrity: null, // 'good', 'warning', or 'bad'
      cleanliness: null,
      labeling: null,
      functionality: null,
      safety: null
    },
    images: []
  });
  const [inspectionImages, setInspectionImages] = useState([]);
  const [assetInspections, setAssetInspections] = useState([]);
  const [assetHistory, setAssetHistory] = useState([]);
  const [showQRCode, setShowQRCode] = useState(false);

  // Get all assets and create zone lookup
  const { allAssets, zonesById, assetTypes, warehousesList, zonesByWarehouse } = useMemo(() => {
    const assets = Object.values(warehouses).flatMap(w => w.assets || []);
    const zones = {};
    const types = new Set();
    const whList = [];
    const zonesByWh = {};
    
    Object.entries(warehouses).forEach(([id, w]) => {
      whList.push({ id, name: w.name || w.displayName || id });
      zonesByWh[id] = w.zones || [];
      
      (w.zones || []).forEach(z => {
        zones[z.id] = z;
      });
      (w.assets || []).forEach(a => {
        if (a.assetType || a.type) {
          types.add(a.assetType || a.type);
        }
      });
    });
    
    return {
      allAssets: assets,
      zonesById: zones,
      assetTypes: Array.from(types).sort(),
      warehousesList: whList,
      zonesByWarehouse: zonesByWh
    };
  }, [warehouses]);

  // Get zones for selected warehouse in the form
  const availableZones = useMemo(() => {
    if (!newAssetForm.warehouseId) return [];
    return zonesByWarehouse[newAssetForm.warehouseId] || [];
  }, [newAssetForm.warehouseId, zonesByWarehouse]);

  // Filter template assets - only show assets marked as templates (is_template = true)
  const templateAssets = useMemo(() => {
    if (!allAssets || allAssets.length === 0) return [];
    
    // Only include assets that are explicitly marked as templates
    return allAssets
      .filter(asset => asset.is_template === true)
      .map(asset => ({
        ...asset,
        displayLabel: `${asset.name || asset.id}${asset.type ? ` - ${asset.type}` : ''}${asset.category ? ` (${asset.category})` : ''}${asset.capacity ? ` - ${asset.capacity}` : ''}`
      }));
  }, [allAssets]);

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }
    
    const template = templateAssets.find(a => a.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      // Auto-fill form fields (excluding ID, warehouse, zone, status, and images)
      setNewAssetForm(prev => ({
        ...prev,
        name: template.name || '',
        type: template.type || template.assetType || '',
        category: template.category || '',
        capacity: template.capacity || '',
        brand: template.brand || ''
        // Keep ID, warehouseId, zoneId, status, images as user-selected
      }));
    }
  };

  // Handle entry mode change
  const handleEntryModeChange = (mode) => {
    setEntryMode(mode);
    if (mode === 'manual') {
      // Clear template selection when switching to manual
      setSelectedTemplate(null);
    }
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setNewAssetForm(prev => {
      const updated = { ...prev, [field]: value };
      // Reset zone if warehouse changes
      if (field === 'warehouseId') {
        updated.zoneId = '';
      }
      return updated;
    });
  };

  // Generate a suggested asset ID
  const generateAssetId = () => {
    const prefix = newAssetForm.type ? newAssetForm.type.charAt(0).toUpperCase() : 'A';
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}-${randomNum}`;
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Preview images (for now, just store file objects)
      const newImages = files.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name
      }));
      setSelectedImages(prev => [...prev, ...newImages]);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Calculate random position within a zone's rect
  const calculatePositionInZone = (zoneId) => {
    if (!zoneId) return null;
    
    const zone = zonesById[zoneId];
    if (!zone || !zone.rect) return null;
    
    const { x, y, width, height } = zone.rect;
    const offset = zone.offset || { dx: 0, dy: 0 };
    
    // Add padding to avoid placing assets at the very edge
    const padding = 20;
    const minX = x + offset.dx + padding;
    const minY = y + offset.dy + padding;
    const maxX = x + offset.dx + width - padding;
    const maxY = y + offset.dy + height - padding;
    
    // Generate random position within zone bounds
    const randomX = Math.floor(Math.random() * (maxX - minX)) + minX;
    const randomY = Math.floor(Math.random() * (maxY - minY)) + minY;
    
    return { x: randomX, y: randomY };
  };

  // Handle opening edit modal
  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
      setNewAssetForm({
        id: asset.id,
        name: asset.name || '',
        type: asset.assetType || asset.type || '',
        category: asset.category || '',
        warehouseId: asset.warehouse_id || '',
        zoneId: asset.zoneId || asset.zone_id || '',
        status: asset.status || 'Active',
        capacity: asset.capacity || '',
        brand: asset.brand || '',
        images: asset.images || [],
        isTemplate: asset.is_template || false
      });
    setSelectedImages([]);
    setShowNewAssetModal(true);
  };

  // Handle assigning PLX tag to expendable asset
  const handlePrintTag = async (asset) => {
    if (!assignFiducialMarker) {
      alert('PLX tag assignment is not available. Please refresh the page.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await assignFiducialMarker(asset.id);
      // Directly open the print modal without alert
      setShowQRCode(true);
      setSelectedAsset(result.asset);
    } catch (error) {
      console.error('Error assigning PLX tag:', error);
      alert(error.message || 'Failed to assign PLX tag. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle opening release marker confirmation modal
  const handleOpenReleaseModal = (asset) => {
    setAssetToRelease(asset);
    setShowReleaseMarkerModal(true);
  };

  // Handle releasing PLX tag
  const handleReleaseMarker = async () => {
    if (!assetToRelease || !releaseFiducialMarker) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await releaseFiducialMarker(assetToRelease.id);
      alert(`PLX tag ${result.released_marker_id} released from asset ${assetToRelease.id}. The tag is now available for reuse.`);
      setShowReleaseMarkerModal(false);
      setAssetToRelease(null);
    } catch (error) {
      console.error('Error releasing PLX tag:', error);
      alert(error.message || 'Failed to release PLX tag. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle canceling PLX tag assignment from QR code modal
  const handleCancelMarkerAssignment = async () => {
    if (!currentAsset || !currentAsset.fiducial_marker_id || !releaseFiducialMarker) {
      return;
    }

    const confirmCancel = window.confirm('Cancel PLX tag assignment? The tag will be released and made available for reuse.');
    if (!confirmCancel) return;

    setIsSubmitting(true);
    try {
      await releaseFiducialMarker(currentAsset.id);
      setShowQRCode(false);
      setSelectedAsset(null);
    } catch (error) {
      console.error('Error canceling PLX tag:', error);
      alert(error.message || 'Failed to cancel PLX tag assignment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submission (create or update)
  const handleCreateAsset = async (e) => {
    e.preventDefault();
    
    if (!newAssetForm.id || !newAssetForm.warehouseId) {
      alert('Asset ID and Warehouse are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingAsset) {
        // Update existing asset
        if (!updateAsset) {
          console.error('updateAsset function is not available');
          alert('Asset update is not available. Please refresh the page.');
          return;
        }

        const result = await updateAsset(editingAsset.id, {
          name: newAssetForm.name || newAssetForm.id,
          type: newAssetForm.type,
          category: newAssetForm.category,
          warehouseId: newAssetForm.warehouseId,
          zoneId: newAssetForm.zoneId || null,
          status: newAssetForm.status,
          capacity: newAssetForm.capacity || null,
          brand: newAssetForm.brand || null,
          position: editingAsset.position || null,
          imageUrl: editingAsset.image || editingAsset.image_url || null,
          images: editingAsset.images || [],
          isTemplate: newAssetForm.isTemplate || false
        });

        console.log('Asset updated successfully:', result);
      } else {
        // Create new asset
    if (!createAsset) {
      console.error('createAsset function is not available');
      alert('Asset creation is not available. Please refresh the page.');
      return;
    }

      // Calculate position within selected zone if zone is selected
      const position = newAssetForm.zoneId 
        ? calculatePositionInZone(newAssetForm.zoneId)
        : null;
      
      console.log('Creating asset with data:', newAssetForm, 'Position:', position);
      const result = await createAsset({
        id: newAssetForm.id,
        name: newAssetForm.name || newAssetForm.id,
        type: newAssetForm.type,
        category: newAssetForm.category,
        warehouseId: newAssetForm.warehouseId,
        zoneId: newAssetForm.zoneId || null,
        status: newAssetForm.status,
        capacity: newAssetForm.capacity || null,
        brand: newAssetForm.brand || null,
        position: position, // Pass calculated position
        isTemplate: newAssetForm.isTemplate || false
      });

      console.log('Asset created successfully:', result);
      }

      // TODO: Upload images to GCS and link to asset
      // This would be handled by the file upload API

      // Reset form and close modal
      setNewAssetForm({
        id: '',
        name: '',
        type: '',
        category: '',
        warehouseId: '',
        zoneId: '',
        status: 'Active',
        capacity: '',
        brand: '',
        images: [],
        isTemplate: false
      });
      setSelectedImages([]);
      setEditingAsset(null);
      setShowNewAssetModal(false);
    } catch (error) {
      console.error(`Error ${editingAsset ? 'updating' : 'creating'} asset:`, error);
      alert(error.message || `Failed to ${editingAsset ? 'update' : 'create'} asset. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Parse CSV file for preview
  const handleCsvFileSelect = async (file) => {
    if (!file) {
      setCsvPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setCsvPreview({ error: 'CSV file must contain at least a header row and one data row.' });
          return;
        }

        // Parse header
        const header = lines[0].split(',').map(col => col.trim().toLowerCase());
        const itemIdIndex = header.indexOf('item_id');
        const nameIndex = header.indexOf('name');

        if (itemIdIndex === -1 || nameIndex === -1) {
          setCsvPreview({ error: 'CSV must contain "item_id" and "name" columns.' });
          return;
        }

        // Parse preview rows (first 10 rows)
        const previewRows = [];
        const maxPreviewRows = Math.min(10, lines.length - 1);
        
        for (let i = 1; i <= maxPreviewRows; i++) {
          const row = lines[i].split(',').map(col => col.trim());
          previewRows.push({
            row: i + 1,
            itemId: row[itemIdIndex] || '',
            name: row[nameIndex] || ''
          });
        }

        setCsvPreview({
          header,
          rows: previewRows,
          totalRows: lines.length - 1,
          hasMore: lines.length - 1 > maxPreviewRows
        });
      } catch (error) {
        setCsvPreview({ error: 'Failed to parse CSV file: ' + error.message });
      }
    };
    reader.readAsText(file);
  };

  // Handle CSV import
  const handleImportAssets = async () => {
    if (!selectedCsvFile || !importForm.warehouseId) {
      alert('Please select a warehouse and CSV file.');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedCsvFile);
      formData.append('warehouseId', importForm.warehouseId);
      if (importForm.type) {
        formData.append('type', importForm.type);
      }

      const result = await api.upload('/api/assets/bulk-import', formData);
      setImportResults(result);
    } catch (error) {
      console.error('Error importing assets:', error);
      const errorMessage = error.message || error.data?.message || 'Failed to import assets. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle scrap asset
  const handleScrapAsset = async (asset) => {
    const reason = prompt('Enter reason for scrapping (optional):');
    if (reason === null) return; // User cancelled
    
    try {
      await scrapAsset(asset.id, reason || null);
      setSelectedAsset(null);
      alert('Asset has been scrapped successfully.');
    } catch (error) {
      console.error('Error scrapping asset:', error);
      alert('Failed to scrap asset. Please try again.');
    }
  };

  // Handle capacity edit
  const handleCapacitySave = async () => {
    if (!currentAsset) return;
    
    try {
      await updateAssetCapacity(currentAsset.id, tempCapacity);
      setEditingCapacity(false);
    } catch (error) {
      console.error('Error updating capacity:', error);
      alert('Failed to update capacity.');
    }
  };

  // Handle name edit
  const handleNameSave = async () => {
    if (!currentAsset || !updateAsset) return;
    
    try {
      await updateAsset(currentAsset.id, {
        name: tempName,
        type: currentAsset.assetType || currentAsset.type,
        category: currentAsset.category,
        warehouseId: currentAsset.warehouse_id,
        zoneId: currentAsset.zoneId,
        status: currentAsset.status,
        capacity: currentAsset.capacity,
        position: currentAsset.position
      });
      setEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update asset name.');
    }
  };

  // Handle ID edit
  const handleIdSave = async () => {
    if (!currentAsset || !updateAsset) return;
    
    // Note: Changing asset ID might require special handling on backend
    // For now, we'll just show an alert that this requires admin action
    alert('Asset ID changes require administrative approval. Please contact support.');
    setEditingId(false);
  };

  // Handle inspection image selection
  const handleInspectionImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name
      }));
      setInspectionImages(prev => [...prev, ...newImages]);
    }
  };

  // Remove inspection image
  const removeInspectionImage = (index) => {
    setInspectionImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Helper function to check if inspection should be sent to vendor
  // Returns true if: at least 1 "bad" OR at least 3 "warning"
  const shouldSendToVendor = (checklist) => {
    if (!checklist) return false;
    const hasBad = Object.values(checklist).some(value => value === 'bad');
    const warningCount = Object.values(checklist).filter(value => value === 'warning').length;
    return hasBad || warningCount >= 3;
  };

  // Handle create inspection
  const handleCreateInspection = async (e) => {
    e.preventDefault();
    
    if (!currentAsset) {
      alert('No asset selected.');
      return;
    }

    if (!createInspection) {
      console.error('createInspection function is not available');
      alert('Inspection creation is not available. Please refresh the page.');
      return;
    }

    // For routine inspections, check if all checklist items are marked
    if (inspectionForm.inspectionType === 'Routine') {
      const checklist = inspectionForm.checklist;
      const allMarked = Object.values(checklist).every(value => value !== null);
      if (!allMarked) {
        alert('Please mark all checklist items as Good, Warning, or Bad.');
        return;
      }
      // Determine overall status based on checklist
      // Inspection fails if: at least 1 "bad" OR at least 3 "warning"
      const hasBad = Object.values(checklist).some(value => value === 'bad');
      const warningCount = Object.values(checklist).filter(value => value === 'warning').length;
      const hasThreeOrMoreWarnings = warningCount >= 3;
      // If any item is bad or 3+ warnings, the inspection fails
      inspectionForm.status = (hasBad || hasThreeOrMoreWarnings) ? 'Failed' : 'Passed';
    }

    setIsSubmitting(true);
    try {
      // Upload images if any
      let imageIds = [];
      if (inspectionImages.length > 0) {
        try {
          const api = (await import('../lib/api')).default;
          const formData = new FormData();
          
          // Add files to FormData
          inspectionImages.forEach(img => {
            formData.append('files', img.file);
          });
          
          // Add assetId metadata
          formData.append('assetId', currentAsset.id);
          formData.append('category', 'inspection');
          
          // Upload files
          const uploadResult = await api.upload('/api/files/upload', formData);
          
          // Extract file IDs from response
          if (uploadResult.files && Array.isArray(uploadResult.files)) {
            imageIds = uploadResult.files.map(file => file.id);
            console.log(`Uploaded ${imageIds.length} inspection images:`, imageIds);
          } else {
            console.warn('Upload response did not contain files array:', uploadResult);
          }
        } catch (uploadError) {
          console.error('Error uploading inspection images:', uploadError);
          // Continue with inspection creation even if image upload fails
          alert('Warning: Failed to upload some images. Inspection will be created without images.');
        }
      }

      const result = await createInspection({
        assetId: currentAsset.id,
        inspectionType: inspectionForm.inspectionType,
        status: inspectionForm.status,
        notes: inspectionForm.notes || null,
        findings: inspectionForm.inspectionType === 'Routine' 
          ? { checklist: inspectionForm.checklist }
          : inspectionForm.findings || {},
        images: imageIds,
        performedBy: null, // Will use current user from auth
        performedByType: 'user'
      });

      console.log('Inspection created:', result);

      // Check for backend warnings or errors
      if (result._error) {
        console.error('Backend error:', result._error);
      }
      if (result._warning) {
        console.warn('Backend warning:', result._warning);
      }

      // Reset form and close modal
      setInspectionForm({
        inspectionType: 'Routine',
        status: 'Pending',
        notes: '',
        findings: {},
        checklist: {
          structuralIntegrity: null,
          cleanliness: null,
          labeling: null,
          functionality: null,
          safety: null
        },
        images: []
      });
      setInspectionImages([]);
      setShowInspectionModal(false);
      
      // Refresh inspections and history
      if (currentAsset) {
        loadAssetInspections(currentAsset.id);
        loadAssetHistory(currentAsset.id);
      }

      // Determine failure reason for better messaging
      let statusMsg = '';
      
      // Check for backend errors/warnings first
      if (result._error) {
        statusMsg = `Inspection created, but there was an error: ${result._error}`;
      } else if (result._warning) {
        statusMsg = `Inspection created, but: ${result._warning}`;
      } else if (inspectionForm.status === 'Failed' && inspectionForm.inspectionType === 'Routine') {
        const checklist = inspectionForm.checklist;
        const hasBad = Object.values(checklist).some(v => v === 'bad');
        const warningCount = Object.values(checklist).filter(v => v === 'warning').length;
        const hasThreeOrMoreWarnings = warningCount >= 3;
        const shouldSend = hasBad || hasThreeOrMoreWarnings;
        
        if (shouldSend) {
          if (hasBad && hasThreeOrMoreWarnings) {
            statusMsg = 'Routine inspection failed. Items marked as Bad and 3+ items marked as Warning were found. Repair ticket created and sent to vendor.';
          } else if (hasBad) {
            statusMsg = 'Routine inspection failed. Items marked as Bad were found. Repair ticket created and sent to vendor.';
          } else {
            statusMsg = 'Routine inspection failed. 3+ items marked as Warning were found. Repair ticket created and sent to vendor.';
          }
        } else {
          statusMsg = 'Routine inspection failed. Inspection logged but not sent to vendor (requires at least 1 Bad item or 3+ Warning items).';
        }
      } else {
        statusMsg = 'Inspection created successfully!';
      }
      alert(statusMsg);
    } catch (error) {
      console.error('Error creating inspection:', error);
      alert(error.message || 'Failed to create inspection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load inspections for current asset
  const loadAssetInspections = async (assetId) => {
    if (!getAssetInspections || !assetId) return;
    try {
      const inspections = await getAssetInspections(assetId);
      setAssetInspections(inspections || []);
    } catch (error) {
      console.error('Error loading inspections:', error);
    }
  };

  // Load history for current asset
  const loadAssetHistory = async (assetId) => {
    if (!getAssetHistory || !assetId) return;
    try {
      const history = await getAssetHistory(assetId);
      setAssetHistory(history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const filteredAssets = useMemo(() => {
    return allAssets.filter(asset => {
      const matchesSearch = 
        (asset.id || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (asset.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (asset.assetType || asset.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (asset.item_id || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const assetType = asset.assetType || asset.type || '';
      const matchesType = filterType === 'All' || assetType === filterType;
      
      const assetStatus = asset.status || 'Active';
      let matchesStatus;
      if (filterStatus === 'All') {
        matchesStatus = true;
      } else if (filterStatus === 'In Repair') {
        // Check if asset has an active repair (not Complete or Finalized)
        const hasActiveRepair = repairs && repairs.some(r => 
          r.asset_id === asset.id && 
          r.status !== 'Complete' && 
          r.status !== 'Finalized'
        );
        matchesStatus = hasActiveRepair;
      } else {
        matchesStatus = assetStatus === filterStatus;
      }
      
      const assetCategory = asset.category || '';
      const matchesCategory = filterCategory === 'All' || assetCategory === filterCategory;
      
      return matchesSearch && matchesType && matchesStatus && matchesCategory;
    });
  }, [allAssets, searchTerm, filterType, filterStatus, filterCategory, repairs]);

  // Pagination
  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);
  const paginatedAssets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAssets.slice(startIndex, endIndex);
  }, [filteredAssets, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterStatus, filterCategory]);

  // Compute current asset - use paginatedAssets for first asset fallback
  const currentAsset = selectedAsset || (paginatedAssets.length > 0 ? paginatedAssets[0] : null);

  // Get instances for expendable templates
  const assetInstances = useMemo(() => {
    if (!currentAsset || currentAsset.category !== 'Expendable' || currentAsset.is_instance) {
      return [];
    }
    // Find all instances created from this template
    return allAssets.filter(asset => 
      asset.is_instance && asset.template_id === currentAsset.id
    ).sort((a, b) => {
      // Sort by creation date (newest first)
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [currentAsset, allAssets]);

  // Open inspection modal
  const openInspectionModal = (inspectionType = 'Routine') => {
    if (!currentAsset) return;
    setInspectionForm({
      inspectionType,
      status: 'Pending',
      notes: '',
      findings: {},
      checklist: {
        structuralIntegrity: null,
        cleanliness: null,
        labeling: null,
        functionality: null,
        safety: null
      },
      images: []
    });
    setInspectionImages([]);
    setShowMiniMap(true); // Show mini-map when opening inspection
    setShowInspectionModal(true);
  };

  // Load inspections and history when asset changes
  React.useEffect(() => {
    if (currentAsset?.id) {
      loadAssetInspections(currentAsset.id);
      loadAssetHistory(currentAsset.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAsset?.id]); // loadAssetInspections and loadAssetHistory intentionally excluded to prevent infinite loops

  // Get active repair for current asset (must be before early return)
  const activeRepair = useMemo(() => {
    if (!currentAsset || !repairs) return null;
    return repairs.find(r => r.asset_id === currentAsset.id && r.status !== 'Complete' && r.status !== 'Finalized') || null;
  }, [currentAsset, repairs]);

  // Get all repairs for current asset (including completed)
  const assetRepairs = useMemo(() => {
    if (!currentAsset || !repairs) return [];
    return repairs.filter(r => r.asset_id === currentAsset.id).sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [currentAsset, repairs]);

  // Check if any asset has an active repair
  const getAssetActiveRepair = (assetId) => {
    if (!repairs) return null;
    return repairs.find(r => r.asset_id === assetId && r.status !== 'Complete' && r.status !== 'Finalized') || null;
  };

  // Get repair status color based on status
  const getRepairStatusColor = (status) => {
    switch (status) {
      case 'Sent to Vendor':
        return {
          border: 'border-red-500/50',
          bg: isDark ? 'bg-red-500/15' : 'bg-red-50',
          text: isDark ? 'text-red-400' : 'text-red-700',
          borderFull: 'border-red-500/50'
        };
      case 'Needs Approval':
        return {
          border: 'border-yellow-500/50',
          bg: isDark ? 'bg-yellow-500/15' : 'bg-yellow-50',
          text: isDark ? 'text-yellow-400' : 'text-yellow-700',
          borderFull: 'border-yellow-500/50'
        };
      case 'In Repair':
        return {
          border: 'border-blue-500/50',
          bg: isDark ? 'bg-blue-500/15' : 'bg-blue-50',
          text: isDark ? 'text-blue-400' : 'text-blue-700',
          borderFull: 'border-blue-500/50'
        };
      case 'Complete':
      case 'Finalized':
        return {
          border: 'border-emerald-500/50',
          bg: isDark ? 'bg-emerald-500/15' : 'bg-emerald-50',
          text: isDark ? 'text-emerald-400' : 'text-emerald-700',
          borderFull: 'border-emerald-500/50'
        };
      default:
        return {
          border: 'border-slate-500/50',
          bg: isDark ? 'bg-slate-500/15' : 'bg-slate-50',
          text: isDark ? 'text-slate-400' : 'text-slate-700',
          borderFull: 'border-slate-500/50'
        };
    }
  };

  // Early return after all hooks
  if (loading && allAssets.length === 0) {
    return <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>Loading assets...</div>;
  }

  // Get zone name for an asset
  const getZoneName = (zoneId) => {
    if (!zoneId) return 'Unassigned';
    const zone = zonesById[zoneId];
    return zone?.name || zone?.displayName || zoneId.replace('zone-', '').toUpperCase();
  };

  // Generate mock beacon ID (in real app, this would come from asset data)
  const getBeaconId = (assetId) => {
    // Generate a mock UUID-like beacon ID from asset ID
    const hash = assetId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `BEACON-${hash.toString(16).padStart(8, '0').toUpperCase()}-${(hash * 7).toString(16).padStart(4, '0').toUpperCase()}-${(hash * 13).toString(16).padStart(4, '0').toUpperCase()}-${(hash * 19).toString(16).padStart(12, '0').toUpperCase()}`;
  };

  // Get history from database or fallback to mock
  const getHistory = (asset) => {
    // Use database history if available
    if (assetHistory && assetHistory.length > 0) {
      return assetHistory.map(h => ({
        date: h.created_at,
        event: h.event_type === 'inspection' 
          ? `${h.event_subtype?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Inspection'}`
          : h.event_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Event',
        details: h.description,
        user: h.performed_by_email || h.performed_by || 'System'
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    // Fallback to mock history
    const history = [];
    if (asset.created_at) {
      history.push({
        date: asset.created_at,
        event: 'Asset Created',
        details: `Asset ${formatAssetId(asset)} was registered in the system`,
        user: 'System'
      });
    }
    if (asset.last_scanned_at) {
      history.push({
        date: asset.last_scanned_at,
        event: 'Asset Scanned',
        details: `Last scanned at ${getZoneName(asset.zoneId)}`,
        user: 'Scanner'
      });
    }
    if (activeRepair) {
      history.push({
        date: activeRepair.created_at,
        event: 'Repair Started',
        details: activeRepair.title || 'Repair workflow initiated',
        user: 'System'
      });
    }
    // Add inspections from assetInspections
    if (assetInspections && assetInspections.length > 0) {
      assetInspections.forEach(inspection => {
        history.push({
          date: inspection.created_at,
          event: `${inspection.inspection_type} Inspection`,
          details: `${inspection.inspection_type} Inspection: ${inspection.status}${inspection.notes ? ' - ' + inspection.notes : ''}`,
          user: inspection.performed_by_email || inspection.performed_by || 'System'
        });
      });
    }
    // Add a default entry if no history
    if (history.length === 0) {
      history.push({
        date: new Date().toISOString(),
        event: 'Asset Registered',
        details: `Asset ${formatAssetId(asset)} is active in the system`,
        user: 'System'
      });
    }
    return history.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  return (
    <FeatureGate 
      user={user} 
      featureKey="asset_management"
      blur={true}
      showUpgradePrompt={true}
      upgradePlanName="Starter"
      customMessage="Asset management requires an active subscription."
    >
      <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-gray-50 text-slate-900'}`}>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Side - Asset Table */}
        <div className={`w-1/2 border-r flex flex-col overflow-hidden ${isDark ? 'border-slate-800 bg-slate-900/50' : 'border-gray-200 bg-white'}`}>
          <div className="p-6 space-y-6 flex-1 overflow-y-auto no-scrollbar">
            {/* Filters and New Asset Button */}
            <div className={`border rounded-xl p-4 space-y-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
              {/* Search Bar - Always on top */}
              <div className="relative w-full">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} size={18} />
                <input 
                  type="text" 
                  placeholder="Search by ID, Name or Type..." 
                  className={`w-full border rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                    isDark 
                      ? 'bg-slate-950 border-slate-800 text-slate-200' 
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Filters and Buttons - Responsive layout */}
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                {/* Filter dropdowns */}
                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                  <Filter className={`flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} size={18} />
                  <select 
                    className={`border rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 cursor-pointer flex-1 min-w-[120px] text-sm ${
                      isDark 
                        ? 'bg-slate-950 border-slate-800 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="All">All Types</option>
                    {assetTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <select 
                    className={`border rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 cursor-pointer flex-1 min-w-[120px] text-sm ${
                      isDark 
                        ? 'bg-slate-950 border-slate-800 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    <option value="All">All Categories</option>
                    {ASSET_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <select 
                    className={`border rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 cursor-pointer flex-1 min-w-[110px] text-sm ${
                      isDark 
                        ? 'bg-slate-950 border-slate-800 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="In Repair">In Repair</option>
                    {ASSET_STATUSES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                      isDark
                        ? 'bg-green-600 hover:bg-green-500 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                    title="Import Assets"
                  >
                    <Upload size={16} className="sm:w-[18px] sm:h-[18px] flex-shrink-0" />
                    <span className="hidden sm:inline">Import Assets</span>
                    <span className="sm:hidden">Import</span>
                  </button>
                  <button
                    onClick={() => setShowNewAssetModal(true)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-500 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    title="New Asset"
                  >
                    <Plus size={16} className="sm:w-[18px] sm:h-[18px] flex-shrink-0" />
                    <span className="hidden sm:inline">New Asset</span>
                    <span className="sm:hidden">New</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className={`border rounded-xl overflow-hidden mb-6 flex flex-col ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`} style={{ height: 'calc(100vh - 280px)' }}>
              {/* Fixed Header */}
              <div className="overflow-x-auto no-scrollbar">
                <table className="w-full text-left border-collapse table-fixed">
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <thead>
                    <tr className={`text-xs uppercase border-b ${isDark ? 'bg-slate-950 text-slate-400 border-slate-800' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      <th className="px-6 py-4 font-medium">Asset ID</th>
                      <th className="px-6 py-4 font-medium">Name</th>
                      <th className="px-6 py-4 font-medium">Type</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                      <th className="px-6 py-4 font-medium">Location</th>
                      <th className="px-6 py-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>
              
              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar">
                <table className="w-full text-left border-collapse table-fixed">
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <tbody className={`divide-y text-sm ${isDark ? 'divide-slate-800' : 'divide-gray-200'}`}>
                    {loading ? (
                      <tr><td colSpan={6} className={`px-6 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Loading assets...</td></tr>
                    ) : paginatedAssets.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={`px-6 py-12 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                          <div className="flex flex-col items-center gap-4">
                            <p>No assets found matching criteria.</p>
                            <button
                              onClick={() => setShowNewAssetModal(true)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                                isDark
                                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              <Plus size={18} />
                              Register New Asset
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedAssets.map(asset => (
                        <tr 
                          key={asset.id} 
                          className={`transition-colors group cursor-pointer ${
                            currentAsset?.id === asset.id 
                              ? isDark ? 'bg-slate-800/70' : 'bg-gray-100' 
                              : isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedAsset(asset)}
                        >
                          <td className="px-6 py-4 font-mono text-blue-500 font-medium">{formatAssetId(asset)}</td>
                          <td className={`px-6 py-4 font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{asset.name || asset.id}</td>
                          <td className={`px-6 py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{asset.assetType || asset.type || 'N/A'}</td>
                          <td className="px-6 py-4">
                            {(() => {
                              // Get all repairs for this asset
                              const assetRepairs = repairs?.filter(r => r.asset_id === asset.id) || [];
                              
                              if (assetRepairs.length > 0) {
                                // Sort by updated_at or created_at to get most recent
                                const sortedRepairs = [...assetRepairs].sort((a, b) => {
                                  const aDate = new Date(a.updated_at || a.created_at || 0);
                                  const bDate = new Date(b.updated_at || b.created_at || 0);
                                  return bDate - aDate; // Most recent first
                                });
                                
                                // Get the most recent repair
                                const mostRecentRepair = sortedRepairs[0];
                                
                                // Show the most recent repair's status
                                const statusColors = getRepairStatusColor(mostRecentRepair.status);
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${statusColors.bg} ${statusColors.borderFull} ${statusColors.text}`}>
                                    {mostRecentRepair.status}
                                  </span>
                                );
                              }
                              
                              // Default to asset status badge
                              return <StatusBadge status={asset.status || 'Active'} size="sm" />;
                            })()}
                          </td>
                          <td className={`px-6 py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{getZoneName(asset.zoneId)}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Show Print Tag for Expendable TEMPLATES (templates can be used to create instances) */}
                              {asset.category === 'Expendable' && !asset.is_instance && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrintTag(asset);
                                  }}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    isDark 
                                      ? 'bg-green-600 hover:bg-green-500 text-white' 
                                      : 'bg-green-600 hover:bg-green-700 text-white'
                                  }`}
                                  title="Print Tag (Creates Instance)"
                                >
                                  <Printer size={14} />
                                </button>
                              )}
                              {/* Show Release Tag for Expendable INSTANCES with a marker */}
                              {asset.category === 'Expendable' && asset.is_instance && asset.fiducial_marker_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReleaseModal(asset);
                                  }}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    isDark 
                                      ? 'bg-orange-600 hover:bg-orange-500 text-white' 
                                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                                  }`}
                                  title="Release Tag (Asset Leaving Facility)"
                                >
                                  <Tag size={14} />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditAsset(asset);
                                }}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  isDark 
                                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-200' 
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                title="Edit Asset"
                              >
                                <Edit2 size={14} />
                              </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAsset(asset);
                                setShowMiniMap(true);
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                isDark 
                                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-200' 
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }`}
                            >
                              <Eye size={14} />
                              View
                            </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Fixed Footer */}
              <div className={`px-6 py-4 border-t flex justify-between items-center text-xs flex-shrink-0 ${isDark ? 'border-slate-800 bg-slate-950 text-slate-500' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                <div className="flex items-center gap-4">
                  <span>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredAssets.length)} of {filteredAssets.length} results
                  </span>
                  <div className="flex items-center gap-2">
                    <label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Per page:</label>
                    <select 
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className={`border rounded px-2 py-1 text-xs ${
                        isDark 
                          ? 'bg-slate-900 border-slate-700 text-slate-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    >
                      <option value={50}>50</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${
                      currentPage === 1
                        ? isDark ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    Previous
                  </button>
                  <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={`px-3 py-1 rounded ${
                      currentPage === totalPages || totalPages === 0
                        ? isDark ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Detail View */}
        <div className={`flex-1 p-6 overflow-y-auto no-scrollbar ${isDark ? 'bg-slate-800/20' : 'bg-gray-50'}`}>
          {currentAsset ? (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Header */}
              <div className={`border rounded-xl p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                    {currentAsset.image ? (
                      <img src={getAssetImageUrl(currentAsset.image)} alt={currentAsset.assetType} className="w-12 h-12 object-contain" />
                    ) : (
                      <Box className={isDark ? 'text-slate-400' : 'text-gray-400'} size={32} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      {editingName ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            className={`flex-1 border rounded-lg px-3 py-1.5 text-2xl font-bold focus:outline-none focus:border-blue-500 ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-slate-900'
                            }`}
                            autoFocus
                          />
                          <button
                            onClick={handleNameSave}
                            className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingName(false);
                              setTempName('');
                            }}
                            className={`p-1.5 rounded-lg ${
                              isDark 
                                ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <h1 
                          className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-2`}
                          onClick={() => {
                            setTempName(currentAsset.name || currentAsset.id);
                            setEditingName(true);
                          }}
                          title="Click to edit"
                        >
                          {currentAsset.name || currentAsset.id}
                          <Edit2 size={16} className="opacity-50" />
                        </h1>
                      )}
                      <StatusBadge status={currentAsset.status || 'Active'} />
                      {currentAsset.category === 'Expendable' && (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          currentAsset.is_instance 
                            ? isDark 
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                              : 'bg-purple-100 text-purple-700 border border-purple-200'
                            : isDark 
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                              : 'bg-blue-100 text-blue-700 border border-blue-200'
                        }`}>
                          {currentAsset.is_instance ? 'Instance' : 'Template'}
                        </span>
                      )}
                    </div>
                    <p className={`font-mono mt-1 flex items-center gap-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {editingId ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={tempId}
                            onChange={(e) => setTempId(e.target.value)}
                            className={`border rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-blue-500 ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-slate-200' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            autoFocus
                          />
                          <button
                            onClick={handleIdSave}
                            className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded"
                          >
                            <Check size={12} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(false);
                              setTempId('');
                            }}
                            className={`p-1 rounded ${
                              isDark 
                                ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span 
                            className="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
                            onClick={() => {
                              setTempId(currentAsset.id);
                              setEditingId(true);
                            }}
                            title="Click to edit"
                          >
                            ID: {formatAssetId(currentAsset)}
                            <Edit2 size={12} className="opacity-50" />
                          </span>
                          <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-slate-600' : 'bg-gray-400'}`}></span>
                          <span>Type: {currentAsset.assetType || currentAsset.type || 'N/A'}</span>
                          {currentAsset.brand && (
                            <>
                              <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-slate-600' : 'bg-gray-400'}`}></span>
                              <span>Brand: {currentAsset.brand}</span>
                            </>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Chat Toggle */}
                  <ChatToggle assetId={currentAsset.id} size="md" />
                  
                  {/* PLX Tag / Print Tag Button */}
                  {currentAsset.category === 'Expendable' && !currentAsset.is_instance ? (
                    // Expendable Template - Show "Print Tag" to create instance
                    <button 
                      onClick={() => handlePrintTag(currentAsset)}
                      disabled={isSubmitting}
                      className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                        isDark 
                          ? 'bg-green-600 border-green-600 hover:bg-green-500 text-white' 
                          : 'bg-green-600 border-green-600 hover:bg-green-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Printer size={16} className="inline mr-2" />
                      Print Tag
                    </button>
                  ) : currentAsset.fiducial_marker_id ? (
                    // Has PLX tag - Show "PLX Tag" button
                  <button 
                    onClick={() => setShowQRCode(true)}
                    className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-white' 
                        : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <QrCode size={16} className="inline mr-2" />
                      PLX Tag
                  </button>
                  ) : null}
                  
                  {/* Only show Request Inspection if asset is Active or doesn't already have an open repair */}
                  {/* Hide Routine Inspection for Expendable assets */}
                  {currentAsset.status !== 'Scrapped' && currentAsset.category !== 'Expendable' && (
                    <>
                      <button 
                        onClick={() => openInspectionModal('Routine')}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                          isDark 
                            ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-white' 
                            : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <ClipboardCheck size={16} className="inline mr-2" />
                        Routine Inspection
                      </button>
                    </>
                  )}
                  
                  {currentAsset.status !== 'Scrapped' && (
                    <button 
                      onClick={() => handleScrapAsset(currentAsset)}
                      className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                        isDark 
                          ? 'bg-slate-900 border-slate-800 hover:border-red-900 hover:bg-red-900/20 hover:text-red-400 text-slate-400' 
                          : 'bg-white border-gray-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-gray-600'
                      }`}
                    >
                      <Ban size={16} className="inline mr-2" />
                      Scrap
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Live Location - Only show if asset is assigned to a zone */}
                  {currentAsset.zoneId && (
                    <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Live Location</h3>
                      <div className="flex flex-col gap-4">
                        <div className={`flex items-center justify-between p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-900/20 text-purple-500 rounded-lg">
                              <MapPin size={20} />
                            </div>
                            <div>
                              <div className={`text-xs uppercase font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Current Zone</div>
                              <div className={isDark ? 'text-slate-200' : 'text-gray-700'}>
                                {getZoneName(currentAsset.zoneId)}
                                {currentAsset.position && (
                                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}> (X: {Math.round(currentAsset.position.x)}, Y: {Math.round(currentAsset.position.y)})</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Mini Map View - Auto-shown */}
                        {currentAsset && (
                          <div 
                            className={`h-64 rounded-lg border relative overflow-hidden group cursor-pointer ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-200'}`}
                            onClick={() => {
                              navigate('/map', { state: { assetId: currentAsset.id, warehouseId: currentAsset.warehouse_id } });
                            }}
                          >
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <div className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isDark ? 'bg-slate-900 text-white' : 'bg-white text-gray-900'}`}>
                                <Maximize2 size={16} />
                                <span className="text-sm font-medium">Click to view on full map</span>
                              </div>
                            </div>
                            <div className="absolute inset-0 z-0">
                              <WarehouseMap
                                data={(() => {
                                  const warehouse = Object.values(warehouses).find(w => w.id === currentAsset.warehouse_id);
                                  return warehouse || { zones: [], assets: [] };
                                })()}
                                selectedAssetId={currentAsset.id}
                                currentWarehouseId={currentAsset.warehouse_id}
                                warehouses={warehouses}
                                onAssetSelect={(id) => {
                                  navigate('/map', { state: { assetId: id, warehouseId: currentAsset.warehouse_id } });
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Instance Information (for Expendable instances) - Show at top */}
                  {currentAsset.category === 'Expendable' && currentAsset.is_instance && (
                    <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <Tag size={20} className="text-purple-500" />
                        Instance Information
                      </h3>
                      <div className="space-y-3">
                        {currentAsset.fiducial_marker_id && (
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-purple-900/20 border-purple-800/30' : 'bg-purple-50 border-purple-200'}`}>
                            <div className={`text-xs uppercase font-semibold mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>PLX Tag ID</div>
                            <div className={`text-3xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                              #{currentAsset.fiducial_marker_id}
                            </div>
                          </div>
                        )}
                        {currentAsset.template_id && (
                          <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Created from Template</div>
                            <div className={`font-mono text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{currentAsset.template_id}</div>
                          </div>
                        )}
                        {currentAsset.item_id && (
                          <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Item ID</div>
                            <div className={`font-mono text-sm font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{currentAsset.item_id}</div>
                          </div>
                        )}
                        {currentAsset.created_at && (
                          <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Instance Created</div>
                            <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                              {new Date(currentAsset.created_at).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Capacity Section */}
                  <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      <Package size={20} className="text-cyan-500" />
                      Capacity
                    </h3>
                    <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                      {editingCapacity ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={tempCapacity}
                            onChange={(e) => setTempCapacity(e.target.value)}
                            placeholder="e.g., 50 units, 100 lbs"
                            className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-slate-200' 
                                : 'bg-white border-gray-200 text-gray-900'
                            }`}
                            autoFocus
                          />
                          <button
                            onClick={handleCapacitySave}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCapacity(false)}
                            className={`px-3 py-2 rounded-lg text-sm ${
                              isDark 
                                ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => {
                            setTempCapacity(currentAsset.capacity || '');
                            setEditingCapacity(true);
                          }}
                          className={`flex items-center justify-between cursor-pointer rounded-lg p-2 -m-2 transition-colors group ${
                            isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'
                          }`}
                        >
                          <div>
                            <div className={`text-xs uppercase font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Asset Capacity</div>
                            <div className={`text-lg font-medium mt-1 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                              {currentAsset.capacity || <span className={`italic ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Not set</span>}
                            </div>
                          </div>
                          <span className={`text-xs opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Click to edit
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Conditional Panels Based on Asset Type */}
                  {currentAsset.category === 'Expendable' && !currentAsset.is_instance ? (
                    <>
                      {/* Expendable Template: Show Instances */}
                      <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            Current Instances
                          </h3>
                          <div className={`px-3 py-1.5 rounded-lg ${isDark ? 'bg-purple-900/30 border border-purple-800/50' : 'bg-purple-50 border border-purple-200'}`}>
                            <span className={`text-sm font-bold ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                              {assetInstances.filter(i => i.fiducial_marker_id).length} Active
                            </span>
                          </div>
                        </div>
                        <div className={`max-h-96 overflow-y-auto space-y-3 ${isDark ? 'scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800' : 'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'}`}>
                          {assetInstances.length > 0 ? (
                            assetInstances.map((instance) => (
                              <div 
                                key={instance.id} 
                                onClick={() => setSelectedAsset(instance)}
                                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                  instance.fiducial_marker_id
                                    ? isDark ? 'bg-purple-900/20 border-purple-800/30 hover:bg-purple-900/30' : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                                    : isDark ? 'bg-slate-950 border-slate-800 hover:bg-slate-900' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      {instance.fiducial_marker_id ? (
                                        <span className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                          PLX Tag #{instance.fiducial_marker_id}
                                        </span>
                                      ) : (
                                        <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                          No Tag Assigned
                                        </span>
                                      )}
                                      <StatusBadge status={instance.status || 'Active'} size="sm" />
                                    </div>
                                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                                      ID: {instance.id.slice(0, 20)}...
                                    </p>
                                  </div>
                                </div>
                                <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-700/50">
                                  {instance.location && (
                                    <div className="flex items-center justify-between">
                                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Location:</span>
                                      <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                        {instance.location}
                                      </span>
                                    </div>
                                  )}
                                  {instance.created_at && (
                                    <div className="flex items-center justify-between">
                                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Created:</span>
                                      <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                        {new Date(instance.created_at).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className={`text-center py-8 rounded-lg border border-dashed ${
                              isDark 
                                ? 'text-slate-500 bg-slate-950 border-slate-800' 
                                : 'text-gray-400 bg-gray-50 border-gray-200'
                            }`}>
                              <p className="mb-2">No instances created yet.</p>
                              <p className="text-xs">Click "Print Tag" to create the first instance.</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Instance Statistics */}
                      <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Statistics
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs uppercase font-semibold mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              Total Instances
                            </div>
                            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {assetInstances.length}
                            </div>
                          </div>
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-purple-900/20 border-purple-800/30' : 'bg-purple-50 border-purple-200'}`}>
                            <div className={`text-xs uppercase font-semibold mb-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                              In Circulation
                            </div>
                            <div className={`text-3xl font-bold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                              {assetInstances.filter(i => i.fiducial_marker_id).length}
                            </div>
                          </div>
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs uppercase font-semibold mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              Released
                            </div>
                            <div className={`text-3xl font-bold ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                              {assetInstances.filter(i => !i.fiducial_marker_id).length}
                            </div>
                          </div>
                          <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className={`text-xs uppercase font-semibold mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              Item ID
                            </div>
                            <div className={`text-xl font-bold font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                              {currentAsset.item_id || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : currentAsset.category === 'Expendable' && currentAsset.is_instance ? (
                    <>
                      {/* Expendable Instance: No additional panels needed (Instance Info shown above) */}
                    </>
                  ) : (
                    <>
                      {/* All Routine Inspections */}
                      <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Routine Inspections</h3>
                        <div className={`max-h-64 overflow-y-auto space-y-3 ${isDark ? 'scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800' : 'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'}`}>
                          {assetInspections.filter(i => i.inspection_type === 'Routine').length > 0 ? (
                            assetInspections
                              .filter(i => i.inspection_type === 'Routine')
                              .map((inspection, idx) => (
                                <div key={idx} className={`p-3 rounded-lg border ${
                                  inspection.status === 'Passed' 
                                    ? isDark ? 'bg-emerald-900/20 border-emerald-800/30' : 'bg-emerald-50 border-emerald-200'
                                    : inspection.status === 'Failed'
                                    ? isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200'
                                    : isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'
                                }`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-sm font-medium ${
                                      inspection.status === 'Passed' 
                                        ? isDark ? 'text-emerald-300' : 'text-emerald-700'
                                        : inspection.status === 'Failed'
                                        ? isDark ? 'text-red-300' : 'text-red-700'
                                        : isDark ? 'text-slate-300' : 'text-gray-700'
                                    }`}>
                                      {inspection.status}
                                    </span>
                                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                                      {inspection.created_at ? new Date(inspection.created_at).toLocaleDateString() : 'N/A'}
                                    </span>
                                  </div>
                                  {inspection.notes && (
                                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{inspection.notes}</p>
                                  )}
                                </div>
                              ))
                          ) : (
                            <div className={`text-center py-4 text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              No routine inspections yet.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Repairs */}
                      <div className={`border rounded-xl p-6 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Repairs</h3>
                          {assetRepairs.filter(r => r.status === 'Complete' || r.status === 'Finalized').length > 0 && (
                            <div className={`px-3 py-1.5 rounded-lg ${isDark ? 'bg-emerald-900/30 border border-emerald-800/50' : 'bg-emerald-50 border border-emerald-200'}`}>
                              <span className={`text-sm font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                ${assetRepairs
                                  .filter(r => r.status === 'Complete' || r.status === 'Finalized')
                                  .reduce((sum, r) => sum + (parseFloat(r.total_repair_cost) || 0), 0)
                                  .toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className={`max-h-64 overflow-y-auto space-y-3 ${isDark ? 'scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800' : 'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'}`}>
                          {assetRepairs.length > 0 ? (
                            assetRepairs
                              .sort((a, b) => {
                                // Sort: active repairs first, then by date (newest first)
                                const aIsActive = a.status !== 'Complete' && a.status !== 'Finalized';
                                const bIsActive = b.status !== 'Complete' && b.status !== 'Finalized';
                                if (aIsActive !== bIsActive) return aIsActive ? -1 : 1;
                                const aDate = new Date(a.updated_at || a.created_at);
                                const bDate = new Date(b.updated_at || b.created_at);
                                return bDate - aDate;
                              })
                              .map((repair) => {
                                const isActive = repair.status !== 'Complete' && repair.status !== 'Finalized';
                                const statusColors = getRepairStatusColor(repair.status);
                                
                                // Get completion date - prefer completed_at, fallback to updated_at or created_at
                                const completionDate = repair.completed_at || 
                                                       (repair.status_history && repair.status_history.find(h => h.status === 'Complete' || h.status === 'Finalized')?.timestamp) ||
                                                       repair.updated_at || 
                                                       repair.created_at;
                                
                                return (
                                  <div key={repair.id} className={`p-4 rounded-lg border ${statusColors.bg} ${statusColors.borderFull}`}>
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                                            {repair.title || 'Repair'}
                                          </span>
                                          <span className={`text-xs px-2 py-0.5 rounded ${statusColors.bg} ${statusColors.borderFull} ${statusColors.text}`}>
                                            {repair.status}
                                          </span>
                                        </div>
                                        {repair.description && (
                                          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                            {repair.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-1.5 mt-2 pt-2 border-t border-slate-700/50">
                                      {isActive ? (
                                        <div className="flex items-center justify-between">
                                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Status:</span>
                                          <span className={`text-xs font-medium ${statusColors.text}`}>
                                            {repair.status}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-between">
                                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Completed:</span>
                                          <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                            {completionDate ? new Date(completionDate).toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit'
                                            }) : 'N/A'}
                                          </span>
                                        </div>
                                      )}
                                      {repair.priority && (
                                        <div className="flex items-center justify-between">
                                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Priority:</span>
                                          <span className={`text-xs font-medium ${
                                            repair.priority === 'High' 
                                              ? isDark ? 'text-red-400' : 'text-red-600'
                                              : repair.priority === 'Medium'
                                              ? isDark ? 'text-amber-400' : 'text-amber-600'
                                              : isDark ? 'text-slate-300' : 'text-gray-700'
                                          }`}>
                                            {repair.priority}
                                          </span>
                                        </div>
                                      )}
                                      {repair.vendor_name && (
                                        <div className="flex items-center justify-between">
                                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Vendor:</span>
                                          <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                            {repair.vendor_name}
                                          </span>
                                        </div>
                                      )}
                                      <div className="mt-3 pt-2 border-t border-slate-700/30">
                                        {isActive ? (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate('/repairs', { state: { repairId: repair.id } });
                                            }}
                                            className={`w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                                              isDark
                                                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                            }`}
                                          >
                                            <ArrowRight size={14} />
                                            View Repair
                                          </button>
                                        ) : (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowSummaryReport(repair);
                                            }}
                                            className={`w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 ${
                                              isDark
                                                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                            }`}
                                          >
                                            <FileText size={14} />
                                            View Summary Report
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                          ) : (
                            <div className={`text-center py-4 text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              No repairs yet.
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* History Timeline */}
                <div className={`border rounded-xl p-6 h-fit ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Activity Log</h3>
                  <div className={`relative border-l ml-3 space-y-8 ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
                    {getHistory(currentAsset).map((event, index) => (
                      <div key={index} className="relative pl-6">
                        <div className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 ${
                          isDark ? 'bg-slate-600 border-slate-900' : 'bg-gray-400 border-white'
                        }`}></div>
                        <div className="flex flex-col">
                          <span className={`text-xs font-mono mb-0.5 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            {new Date(event.date).toLocaleDateString()} {new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                          <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{event.event}</span>
                          <span className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{event.details}</span>
                          {event.user && <span className={`text-[10px] mt-1 uppercase tracking-wide ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>By: {event.user}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`flex items-center justify-center h-full ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Select an asset to view details</div>
          )}
        </div>
      </div>

      {/* Register New Asset / Edit Asset Modal */}
      {showNewAssetModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`border rounded-xl w-full max-w-lg mx-4 shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {editingAsset ? 'Edit Asset' : 'Register New Asset'}
                </h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {editingAsset ? 'Update asset information' : 'Add a new asset to your inventory'}
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowNewAssetModal(false);
                  setEditingAsset(null);
                  setNewAssetForm({
                    id: '',
                    name: '',
                    type: '',
                    category: '',
                    warehouseId: '',
                    zoneId: '',
                    status: 'Active',
                    capacity: '',
                    brand: '',
                    images: []
                  });
                  setSelectedImages([]);
                  setEntryMode('manual');
                  setSelectedTemplate(null);
                }}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateAsset} className="p-6 space-y-4">
              {/* Entry Mode Selection */}
              {!editingAsset && (
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Entry Mode
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="entryMode"
                        value="manual"
                        checked={entryMode === 'manual'}
                        onChange={(e) => handleEntryModeChange(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>Manual Entry</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="entryMode"
                        value="template"
                        checked={entryMode === 'template'}
                        onChange={(e) => handleEntryModeChange(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>Use Template</span>
                    </label>
                  </div>
                  
                  {/* Template Selection */}
                  {entryMode === 'template' && (
                    <div className="mt-4">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        Select Template Asset
                      </label>
                      {templateAssets.length === 0 ? (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-900/50 border-slate-700 text-slate-400' : 'bg-gray-100 border-gray-300 text-gray-600'}`}>
                          <p className="text-sm">No template assets available. Create some assets first to use them as templates.</p>
                        </div>
                      ) : (
                        <SearchableSelect
                          options={templateAssets}
                          value={selectedTemplate?.id || ''}
                          onChange={handleTemplateSelect}
                          placeholder="Search for a similar asset..."
                          searchPlaceholder="Search by name, type, category, or capacity..."
                          getOptionLabel={(opt) => opt.displayLabel || opt.name || opt.id}
                          getOptionValue={(opt) => opt.id}
                          isDark={isDark}
                        />
                      )}
                      {selectedTemplate && (
                        <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            Template Preview:
                          </p>
                          <div className="space-y-1 text-sm">
                            {selectedTemplate.name && (
                              <div className={`flex ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                <span className="font-medium w-20">Name:</span>
                                <span>{selectedTemplate.name}</span>
                              </div>
                            )}
                            {selectedTemplate.type && (
                              <div className={`flex ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                <span className="font-medium w-20">Type:</span>
                                <span>{selectedTemplate.type}</span>
                              </div>
                            )}
                            {selectedTemplate.category && (
                              <div className={`flex ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                <span className="font-medium w-20">Category:</span>
                                <span>{selectedTemplate.category}</span>
                              </div>
                            )}
                            {selectedTemplate.capacity && (
                              <div className={`flex ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                <span className="font-medium w-20">Capacity:</span>
                                <span>{selectedTemplate.capacity}</span>
                              </div>
                            )}
                            {selectedTemplate.brand && (
                              <div className={`flex ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                <span className="font-medium w-20">Brand:</span>
                                <span>{selectedTemplate.brand}</span>
                              </div>
                            )}
                          </div>
                          <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            Fields above will be auto-filled. You can still edit them.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* Asset ID */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Asset ID <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAssetForm.id}
                    onChange={(e) => handleFormChange('id', e.target.value)}
                    placeholder="e.g., R-1001"
                    className={`flex-1 border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    required
                    disabled={!!editingAsset}
                  />
                  {!editingAsset && (
                  <button
                    type="button"
                    onClick={() => handleFormChange('id', generateAssetId())}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDark 
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Generate
                  </button>
                  )}
                </div>
                {editingAsset && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                    Asset ID cannot be changed
                  </p>
                )}
              </div>

              {/* Asset Name and Brand Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Asset Name
                  </label>
                  <input
                    type="text"
                    value={newAssetForm.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Optional display name"
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Brand
                  </label>
                  <input
                    type="text"
                    value={newAssetForm.brand}
                    onChange={(e) => handleFormChange('brand', e.target.value)}
                    placeholder="e.g., Toyota, Caterpillar, etc."
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {/* Type and Category Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Asset Type
                  </label>
                  <select
                    value={newAssetForm.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    <option value="">Select Type</option>
                    {ASSET_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Category
                  </label>
                  <select
                    value={newAssetForm.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    <option value="">Select Category</option>
                    {ASSET_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Warehouse and Zone Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Warehouse <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newAssetForm.warehouseId}
                    onChange={(e) => handleFormChange('warehouseId', e.target.value)}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    required
                  >
                    <option value="">Select Warehouse</option>
                    {warehousesList.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Zone
                  </label>
                  <select
                    value={newAssetForm.zoneId}
                    onChange={(e) => handleFormChange('zoneId', e.target.value)}
                    disabled={!newAssetForm.warehouseId}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    <option value="">Select Zone (Optional)</option>
                    {availableZones.map(zone => (
                      <option key={zone.id} value={zone.id}>{zone.name || zone.displayName || zone.id}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Capacity and Initial Status Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    <span className="flex items-center gap-1">
                      <Package size={14} />
                      Capacity
                    </span>
                  </label>
                  <input
                    type="text"
                    value={newAssetForm.capacity}
                    onChange={(e) => handleFormChange('capacity', e.target.value)}
                    placeholder="e.g., 50 units, 100 lbs, 25 gallons"
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Describe what this asset can hold</p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Initial Status
                  </label>
                  <select
                    value={newAssetForm.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    {ASSET_STATUSES.filter(s => s !== 'Scrapped').map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  <span className="flex items-center gap-1">
                    <Image size={14} />
                    Images (Optional)
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full px-4 py-3 border border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    isDark 
                      ? 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <Upload size={18} />
                  Click to upload images
                </button>
                
                {/* Image Previews */}
                {selectedImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.preview}
                          alt={img.name}
                          className={`w-16 h-16 object-cover rounded-lg border ${isDark ? 'border-slate-700' : 'border-gray-300'}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeSelectedImage(index)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save as Template Checkbox */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newAssetForm.isTemplate || false}
                    onChange={(e) => handleFormChange('isTemplate', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Save as Template
                  </span>
                </label>
                <p className={`text-xs mt-1 ml-6 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                  {editingAsset 
                    ? 'Mark this asset as a template to make it available for creating similar assets.'
                    : 'This asset will be available as a template for creating similar assets in the future.'}
                </p>
              </div>

              {/* Form Actions */}
              <div className={`flex gap-3 pt-4 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewAssetModal(false);
                    setEditingAsset(null);
                    setNewAssetForm({
                      id: '',
                      name: '',
                      type: '',
                      category: '',
                      warehouseId: '',
                      zoneId: '',
                      status: 'Active',
                      capacity: '',
                      brand: '',
                      images: [],
                      isTemplate: false
                    });
                    setSelectedImages([]);
                    setEntryMode('manual');
                    setSelectedTemplate(null);
                  }}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDark 
                      ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {editingAsset ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingAsset ? (
                        <>
                          <Check size={16} />
                          Update Asset
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Create Asset
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Assets Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`border rounded-xl w-full max-w-lg mx-4 shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Import Assets from CSV</h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Upload a CSV file with item_id and name columns</p>
              </div>
              <button 
                onClick={() => {
                  setShowImportModal(false);
                  setSelectedCsvFile(null);
                  setCsvPreview(null);
                  setImportResults(null);
                  setImportForm({ warehouseId: '', type: '' });
                }}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            {/* Modal Form */}
            <div className="p-6 space-y-4">
              {!importResults ? (
                <>
                  {/* Warehouse Selection */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      Warehouse <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={importForm.warehouseId}
                      onChange={(e) => setImportForm({ ...importForm, warehouseId: e.target.value })}
                      className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                        isDark 
                          ? 'bg-slate-950 border-slate-700 text-slate-200' 
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}
                      required
                    >
                      <option value="">Select Warehouse</option>
                      {warehousesList.map(wh => (
                        <option key={wh.id} value={wh.id}>{wh.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Asset Type Selection */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      Asset Type (Optional)
                    </label>
                    <select
                      value={importForm.type}
                      onChange={(e) => setImportForm({ ...importForm, type: e.target.value })}
                      className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                        isDark 
                          ? 'bg-slate-950 border-slate-700 text-slate-200' 
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}
                    >
                      <option value="">Select Type</option>
                      {ASSET_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      This will be applied to all imported assets
                    </p>
                  </div>

                  {/* CSV File Upload */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      CSV File <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={csvFileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setSelectedCsvFile(file);
                        handleCsvFileSelect(file);
                      }}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => csvFileInputRef.current?.click()}
                      className={`w-full px-4 py-3 border border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        isDark 
                          ? 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600' 
                          : 'bg-gray-50 border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <Upload size={18} />
                      {selectedCsvFile ? selectedCsvFile.name : 'Click to upload CSV file'}
                    </button>
                    <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      Required columns: <span className="font-mono">item_id</span>, <span className="font-mono">name</span>
                    </p>
                  </div>

                  {/* CSV Preview */}
                  {csvPreview && (
                    <div className={`border rounded-lg overflow-hidden ${isDark ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                      {csvPreview.error ? (
                        <div className={`p-4 ${isDark ? 'bg-red-950/20 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          <p className="text-sm font-medium">Error parsing CSV:</p>
                          <p className="text-xs mt-1">{csvPreview.error}</p>
                        </div>
                      ) : (
                        <>
                          <div className={`p-3 border-b ${isDark ? 'border-slate-800 bg-slate-900' : 'border-gray-200 bg-gray-100'}`}>
                            <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              Preview ({csvPreview.totalRows} {csvPreview.totalRows === 1 ? 'asset' : 'assets'})
                              {csvPreview.hasMore && <span className="text-xs font-normal ml-2 text-gray-500">(showing first 10)</span>}
                            </h4>
                          </div>
                          <div className="overflow-x-auto max-h-60 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className={`border-b ${isDark ? 'bg-slate-900 border-slate-800 text-slate-400' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                                  <th className="px-3 py-2 text-left font-medium">Row</th>
                                  <th className="px-3 py-2 text-left font-medium">Item ID</th>
                                  <th className="px-3 py-2 text-left font-medium">Name</th>
                                </tr>
                              </thead>
                              <tbody className={`divide-y ${isDark ? 'divide-slate-800' : 'divide-gray-200'}`}>
                                {csvPreview.rows.map((row, idx) => (
                                  <tr key={idx} className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                                    <td className="px-3 py-2">{row.row}</td>
                                    <td className="px-3 py-2 font-mono">{row.itemId || <span className="text-red-500">(empty)</span>}</td>
                                    <td className="px-3 py-2">{row.name || <span className="text-red-500">(empty)</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className={`flex gap-3 pt-4 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowImportModal(false);
                        setSelectedCsvFile(null);
                        setCsvPreview(null);
                        setImportForm({ warehouseId: '', type: '' });
                      }}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                        isDark 
                          ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleImportAssets}
                      disabled={isSubmitting || !importForm.warehouseId || !selectedCsvFile || (csvPreview && csvPreview.error)}
                      className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload size={16} />
                          Import Assets
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Success Message */}
                  {importResults.results.success.length > 0 && (
                    <div className={`p-4 rounded-lg border-2 ${isDark ? 'bg-green-950/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                          <Check size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className={`font-semibold text-lg ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                            Successfully Imported!
                          </h3>
                          <p className={`text-sm mt-1 ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                            {importResults.results.success.length} of {importResults.results.total} assets imported successfully
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Import Results */}
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-950' : 'bg-gray-50'}`}>
                      <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Import Summary</h3>
                      <div className="space-y-1 text-sm">
                        <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                          Total rows: <span className="font-semibold">{importResults.results.total}</span>
                        </p>
                        <p className="text-green-500">
                          Successfully imported: <span className="font-semibold">{importResults.results.success.length}</span>
                        </p>
                        {importResults.results.errors.length > 0 && (
                          <p className="text-red-500">
                            Errors: <span className="font-semibold">{importResults.results.errors.length}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Errors List */}
                    {importResults.results.errors.length > 0 && (
                      <div className={`p-4 rounded-lg max-h-60 overflow-y-auto ${isDark ? 'bg-red-950/20 border border-red-900' : 'bg-red-50 border border-red-200'}`}>
                        <h4 className={`font-semibold mb-2 text-sm ${isDark ? 'text-red-400' : 'text-red-700'}`}>Errors:</h4>
                        <div className="space-y-1 text-xs">
                          {importResults.results.errors.map((err, idx) => (
                            <p key={idx} className={isDark ? 'text-red-300' : 'text-red-600'}>
                              Row {err.row} ({err.itemId}): {err.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Close Button */}
                  <div className={`pt-4 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowImportModal(false);
                        setSelectedCsvFile(null);
                        setCsvPreview(null);
                        setImportResults(null);
                        setImportForm({ warehouseId: '', type: '' });
                        // Reload page to show newly imported assets
                        if (importResults?.results?.success?.length > 0) {
                          window.location.reload();
                        }
                      }}
                      className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors ${
                        isDark 
                          ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {importResults?.results?.success?.length > 0 ? 'Close & Refresh' : 'Close'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inspection Modal */}
      {showInspectionModal && currentAsset && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`border rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <div>
                <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <ClipboardCheck size={20} className="text-blue-500" />
                  {inspectionForm.inspectionType === 'Routine' ? 'Routine Inspection' : 'Repair Inspection'}
                </h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {inspectionForm.inspectionType === 'Routine' 
                    ? 'Quick check done by anyone or AI cameras'
                    : 'Inspection done when something is actually wrong'}
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowInspectionModal(false);
                  setInspectionImages([]);
                }}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            {/* Asset Info */}
            <div className={`px-6 py-4 border-b ${isDark ? 'bg-slate-800/30 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-200'}`}>
                  <Box className={isDark ? 'text-slate-400' : 'text-gray-400'} size={24} />
                </div>
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{currentAsset.name || currentAsset.id}</div>
                  <div className={`text-xs flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <span className="font-mono">{formatAssetId(currentAsset)}</span>
                    <span>•</span>
                    <span>{currentAsset.assetType || currentAsset.type || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateInspection} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Inspection Type (read-only, set by button) */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Inspection Type
                </label>
                <div className={`w-full border rounded-lg px-4 py-2.5 ${
                  isDark 
                    ? 'bg-slate-950 border-slate-700 text-slate-200' 
                    : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  {inspectionForm.inspectionType}
                </div>
              </div>

              {/* Routine Inspection Checklist */}
              {inspectionForm.inspectionType === 'Routine' ? (
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Inspection Checklist <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {[
                      { key: 'structuralIntegrity', label: 'Structural Integrity' },
                      { key: 'cleanliness', label: 'Cleanliness' },
                      { key: 'labeling', label: 'Labeling & Identification' },
                      { key: 'functionality', label: 'Functionality' },
                      { key: 'safety', label: 'Safety Compliance' }
                    ].map((item) => (
                      <div key={item.key} className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDark ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                          {item.label}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setInspectionForm(prev => ({
                                ...prev,
                                checklist: { ...prev.checklist, [item.key]: 'good' }
                              }));
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              inspectionForm.checklist[item.key] === 'good'
                                ? 'bg-emerald-600 text-white border-2 border-emerald-500'
                                : isDark
                                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600'
                                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                            }`}
                          >
                            Good
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setInspectionForm(prev => ({
                                ...prev,
                                checklist: { ...prev.checklist, [item.key]: 'warning' }
                              }));
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              inspectionForm.checklist[item.key] === 'warning'
                                ? 'bg-amber-500 text-white border-2 border-amber-400'
                                : isDark
                                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600'
                                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                            }`}
                          >
                            Warning
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setInspectionForm(prev => ({
                                ...prev,
                                checklist: { ...prev.checklist, [item.key]: 'bad' }
                              }));
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              inspectionForm.checklist[item.key] === 'bad'
                                ? 'bg-red-600 text-white border-2 border-red-500'
                                : isDark
                                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600'
                                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                            }`}
                          >
                            Bad
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                    All items must be marked. Inspection fails if any item is marked as Bad or if 3+ items are marked as Warning.
                  </p>
                </div>
              ) : (
                /* Repair Inspection - Status Selection */
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Inspection Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={inspectionForm.status}
                    onChange={(e) => setInspectionForm(prev => ({ ...prev, status: e.target.value }))}
                    className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer ${
                      isDark 
                        ? 'bg-slate-950 border-slate-700 text-slate-200' 
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                    required
                  >
                    <option value="Pending">Pending</option>
                    <option value="Passed">Passed</option>
                    <option value="Failed">Failed</option>
                  </select>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Notes
                </label>
                <textarea
                  value={inspectionForm.notes}
                  onChange={(e) => setInspectionForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Enter inspection notes, findings, or observations..."
                  rows={3}
                  className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none ${
                    isDark 
                      ? 'bg-slate-950 border-slate-700 text-slate-200' 
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                />
              </div>

              {/* Attachments Section */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  <span className="flex items-center gap-1">
                    <Image size={14} />
                    Attachments / Pictures
                  </span>
                </label>
                <input
                  ref={inspectionFileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  multiple
                  onChange={handleInspectionImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => inspectionFileInputRef.current?.click()}
                  className={`w-full px-4 py-3 border border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    isDark 
                      ? 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600' 
                      : 'bg-gray-50 border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <Upload size={18} />
                  Click to upload attachments
                </button>
                
                {/* Image Previews */}
                {inspectionImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {inspectionImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.preview}
                          alt={img.name}
                          className={`w-16 h-16 object-cover rounded-lg border ${isDark ? 'border-slate-700' : 'border-gray-300'}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeInspectionImage(index)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Notice */}
              {inspectionForm.inspectionType === 'Routine' ? (
                <div className={`flex items-start gap-3 p-3 rounded-lg ${
                  shouldSendToVendor(inspectionForm.checklist)
                    ? 'bg-amber-900/20 border border-amber-900/30'
                    : 'bg-blue-900/20 border border-blue-900/30'
                }`}>
                  <ClipboardCheck size={18} className={`mt-0.5 ${
                    shouldSendToVendor(inspectionForm.checklist)
                      ? 'text-amber-400'
                      : 'text-blue-400'
                  }`} />
                  <div className={`text-xs ${
                    shouldSendToVendor(inspectionForm.checklist)
                      ? 'text-amber-300'
                      : 'text-blue-300'
                  }`}>
                    {shouldSendToVendor(inspectionForm.checklist) ? (
                      <>
                        <p className="font-medium mb-1">⚠️ Failed Routine Inspection</p>
                        <p>This inspection will be sent to vendor for repair. Asset will appear in the "Needs Repair" section.</p>
                      </>
                    ) : Object.values(inspectionForm.checklist).every(v => v !== null) ? (
                      <>
                        <p className="font-medium mb-1">✅ All Items Good</p>
                        <p>Inspection will be logged as passed in asset history.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium mb-1">Inspection Checklist</p>
                        <p>Mark each item as Good (Green), Warning (Yellow), or Bad (Red). Inspection fails if any item is Bad or if 3+ items are Warning.</p>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`flex items-start gap-3 p-3 rounded-lg bg-blue-900/20 border border-blue-900/30`}>
                  <ClipboardCheck size={18} className="text-blue-400 mt-0.5" />
                  <div className="text-xs text-blue-300">
                    <p className="font-medium mb-1">What happens next?</p>
                    <ul className="space-y-1">
                      <li>• Inspection will be logged in asset history</li>
                      <li>• Results will be recorded for repair tracking</li>
                      <li>• View inspection history in the Activity Log</li>
                    </ul>
                  </div>
                </div>
              )}

              </div>
              {/* Form Actions */}
              <div className={`flex gap-3 p-6 pt-4 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                <button
                  type="button"
                  onClick={() => {
                    setShowInspectionModal(false);
                    setInspectionImages([]);
                  }}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDark 
                      ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Creating...
                    </>
                  ) : inspectionForm.inspectionType === 'Routine' && shouldSendToVendor(inspectionForm.checklist) ? (
                    <>
                      <ClipboardCheck size={16} />
                      Create Inspection & Send to Vendor for Repair
                    </>
                  ) : (
                    <>
                      <ClipboardCheck size={16} />
                      Create Inspection
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Repair Summary Report Modal */}
      {showSummaryReport && (
        <RepairSummaryReport
          ticket={showSummaryReport}
          onClose={() => setShowSummaryReport(null)}
          isDark={isDark}
        />
      )}

      {/* ArUco Marker Modal */}
      {showQRCode && currentAsset && (
        <AssetQRCode
          asset={currentAsset}
          isDark={isDark}
          onClose={() => setShowQRCode(false)}
          onCancel={currentAsset.category === 'Expendable' ? handleCancelMarkerAssignment : null}
        />
      )}

      {/* Release Marker Confirmation Modal */}
      {showReleaseMarkerModal && assetToRelease && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`border rounded-xl w-full max-w-md mx-4 shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-600/20' : 'bg-orange-100'}`}>
                  <Tag size={20} className={isDark ? 'text-orange-400' : 'text-orange-600'} />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Release PLX Tag
                  </h2>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    Asset leaving facility
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowReleaseMarkerModal(false);
                  setAssetToRelease(null);
                }}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-950 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-sm mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  <p className="font-medium mb-1">Asset: {assetToRelease.id}</p>
                  <p className="text-xs">{assetToRelease.name || 'No name'}</p>
                  {assetToRelease.fiducial_marker_id && (
                    <p className={`text-xs mt-2 font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      Marker ID: {assetToRelease.fiducial_marker_id}
                    </p>
                  )}
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-900/30' : 'bg-orange-50 border border-orange-200'}`}>
                <p className={`text-sm font-medium mb-2 ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                  ⚠️ Confirm Asset Departure
                </p>
                <p className={`text-xs ${isDark ? 'text-orange-300' : 'text-orange-600'}`}>
                  Used when an expendable asset leaves your facility permanently or temporarily. The marker can be reassigned to a new incoming asset.
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className={`flex gap-3 p-6 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => {
                  setShowReleaseMarkerModal(false);
                  setAssetToRelease(null);
                }}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  isDark 
                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReleaseMarker}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Releasing...
                  </>
                ) : (
                  <>
                    <Tag size={16} />
                    Release Marker
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </FeatureGate>
  );
}
